import { z } from 'zod/v4'
import {
  type ApiHydraResponse,
} from '@services/api/declarations'

import { ClubResourceWithRolesSchema } from '@services/Legacy/declarations'
import { ExcludeExternalUserKindSchema } from '@libs/fsc/types/user'

// ***
// SCHEMAS
// ***

// ***
// REQUESTS
// ***

// PATCH
export const PatchUserChangePasswordRequestSchema = z.object({
  data: z.object({
    currentPassword: z.string(),
    newPassword: z.string(),
  }),
})

// POST
const PasswordResetMethodSchema = z.enum(['email', 'whatsapp', 'sms'])
export const PasswordResetMethod = PasswordResetMethodSchema.enum
export const PostUserPasswordResetRequestSchema = z.object({
  data: z.object({
    username: z.string(),
    method: PasswordResetMethodSchema,
  }),
})

export const PostUserRegisterRequestSchema = z.object({
  data: z.object({
    username: z.string(),
    email: z.string(),
    password: z.string(),
    confirmPassword: z.string(),
  }),
})

export const PostUserPasswordResetValidateRequestSchema = z.object({
  data: z.object({
    username: z.string(),
    code: z.string(),
  }),
})

export const PostUserPasswordResetConfirmRequestSchema = z.object({
  data: z.object({
    username: z.string(),
    code: z.string(),
    newPassword: z.string(),
    confirmNewPassword: z.string(),
  }),
})

export const PostCreateProfileRequestSchema = z.object({
  params: z.object({
    profileType: ExcludeExternalUserKindSchema,
  }),
  data: z.object({
    user: z.object({
      username: z.string(),
    }),
    name: z.string(),
    surname: z.string(),
    email: z.email(),
    dni: z.string(),
    club: z.string().optional(),
  }),
})

export const GetOrphanedManagersRequestSchema = z.object({
  query: z.object({
    club: z.string().optional(),
  }).optional(),
})

export const PostValidateEmailRequestSchema = z.object({
  data: z.object({
    email: z.email(),
  }),
})

export const PostConfirmEmailRequestSchema = z.object({
  data: z.object({
    hash: z.string(),
  }),
})

// ***
// DATA SCHEMAS
// ***

export const UserProfileSchema = z.object({
  clubs: z.array(ClubResourceWithRolesSchema),
  username: z.string(),
  email: z.union([z.email(), z.string()]),
  admin: z.boolean(),
  error: z.object({
    code: z.number(),
    message: z.string(),
  }).optional(),
})

export const RegisterUserResponseSchema = z.object({
  token: z.jwt(),
  token_ttl: z.number(),
})

export const ApiUserChangePasswordSchema = z.object({
  password: z.string(),
})

export const OrphanedManagerSchema = z.object({
  hasOrphanedProfiles: z.boolean(),
})

// ***
// EXPORTED TYPES
// ***

export type PatchUserChangePasswordRequest = z.infer<typeof PatchUserChangePasswordRequestSchema>
export type PostUserPasswordResetRequest = z.infer<typeof PostUserPasswordResetRequestSchema>
export type PostUserRegisterRequest = z.infer<typeof PostUserRegisterRequestSchema>
export type PostUserPasswordResetValidateRequest = z.infer<typeof PostUserPasswordResetValidateRequestSchema>
export type PostUserPasswordResetConfirmRequest = z.infer<typeof PostUserPasswordResetConfirmRequestSchema>
export type PostCreateProfileRequest = z.infer<typeof PostCreateProfileRequestSchema>
export type GetOrphanedManagersRequest = z.infer<typeof GetOrphanedManagersRequestSchema>
export type PostValidateEmailRequest = z.infer<typeof PostValidateEmailRequestSchema>
export type PostConfirmEmailRequest = z.infer<typeof PostConfirmEmailRequestSchema>

export type UserProfile = z.infer<typeof UserProfileSchema>
export type RegisterUserResponse = z.infer<typeof RegisterUserResponseSchema>
export type ApiUserChangePassword = z.infer<typeof ApiUserChangePasswordSchema>
export type OrphanedManager = z.infer<typeof OrphanedManagerSchema>

// ***
// MESSAGES
// ***

export enum PatchUserChangePasswordErrorMessages {
  BadCurrentPassword = 'Password is wrong',
}

// ***
// STORE
// ***

export interface UserStoreShape {
  getUserByUsername: unknown,
  getUserProfile: ApiHydraResponse<UserProfile>,
  patchUserChangePassword: ApiUserChangePassword,
  postUserPasswordResetConfirm: RegisterUserResponse,
  postUserRegister: ApiHydraResponse<RegisterUserResponse>,
  postUserPasswordResetRequest: unknown,
  postUserPasswordResetValidate: unknown,
  getOrphanedManagers: ApiHydraResponse<OrphanedManager>,
  postConfirmEmail: unknown,
  postValidateEmail: unknown,
}
